import './shared-nav';
import type {
  ChatMessage,
  BotSettings,
  PriceResponse,
  SearchResponse,
  ImageResponse,
  MemeResponse,
  ChatResponse,
  AOXBotAPI
} from './types';

import { blogUI } from './blog-ui';
import { XamanAuth } from './xaman-auth';
import { NFTViewer } from './nft-viewer';
import { showError } from './modal';
import { API_BASE_URL } from './config';

// Bot state
let botOpen = false;
let isTyping = false;
let chatHistory: ChatMessage[] = [];

// Xaman wallet and NFT viewer
const xamanAuth = new XamanAuth();
const nftViewer = new NFTViewer(xamanAuth);

// Initialize bot on page load
document.addEventListener('DOMContentLoaded', () => {
  initializeBotWidget();
  loadChatHistory();
  initializeWalletSection();

  // Initialize blog
  blogUI.initialize().catch(err => {
    console.error('Failed to initialize blog:', err);
  });
});

function initializeBotWidget(): void {
  // Create bot widget HTML
  const widgetHTML = `
    <div class="bot-widget">
      <button class="bot-toggle" onclick="toggleBot()" aria-label="Open chat">
        <img src="img/logo.png" alt="AOX Bot" style="width: 40px; height: 40px;">
      </button>

      <div class="bot-chat" id="botChat">
        <div class="chat-header">
          <h3><img src="img/logo.png" style="width: 25px; height: 25px;"> Zyx-9 Assistant</h3>
          <button class="chat-close" onclick="toggleBot()">×</button>
        </div>

        <div class="chat-messages" id="chatMessages">
          <div class="message bot">
            👽 Greetings, Earthling! I am Zyx-9 from the XRP-7 star system. How may I assist you today?
          </div>
        </div>

        <div class="chat-input-container">
          <div class="chat-commands">
            <button class="command-btn" onclick="setCommand('ask')">Ask</button>
            <button class="command-btn" onclick="setCommand('search')">Search</button>
            <button class="command-btn" onclick="setCommand('image')">Image</button>
            <button class="command-btn" onclick="setCommand('price')">Price</button>
            <button class="command-btn" onclick="setCommand('meme')">Meme</button>
          </div>

          <div class="file-upload-area" id="fileUploadArea" style="display: none;">
            <input type="file" id="imageInput" accept="image/*" style="display: none;">
            <button class="upload-btn" onclick="document.getElementById('imageInput').click()">
              📸 Upload Image for Meme
            </button>
            <div id="imagePreview"></div>
          </div>

          <div class="chat-input-wrapper">
            <input type="text"
                   class="chat-input"
                   id="chatInput"
                   placeholder="Type your message..."
                   onkeypress="handleKeyPress(event)">
            <button class="chat-send" onclick="sendMessage()" id="sendBtn">
              <svg viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  // Add widget to page
  document.body.insertAdjacentHTML('beforeend', widgetHTML);

  // Set up image input handler
  const imageInput = document.getElementById('imageInput') as HTMLInputElement;
  if (imageInput) {
    imageInput.addEventListener('change', handleImageUpload);
  }
}

function toggleBot(): void {
  const chat = document.getElementById('botChat');
  if (!chat) return;

  botOpen = !botOpen;
  chat.classList.toggle('active');

  if (botOpen) {
    const chatInput = document.getElementById('chatInput') as HTMLInputElement;
    chatInput?.focus();
    // Show greeting if first time
    if (chatHistory.length === 0) {
      animateTyping();
    }
  }
}

function setCommand(command: string): void {
  const input = document.getElementById('chatInput') as HTMLInputElement;
  if (!input) return;

  input.value = `/${command} `;
  input.focus();

  const fileUploadArea = document.getElementById('fileUploadArea');
  if (!fileUploadArea) return;

  // Show file upload for meme command
  if (command === 'meme') {
    fileUploadArea.style.display = 'block';
  } else {
    fileUploadArea.style.display = 'none';
  }
}

function handleKeyPress(event: KeyboardEvent): void {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    sendMessage();
  }
}

function handleImageUpload(event: Event): void {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file) {
    const reader = new FileReader();
    reader.onload = function(e: ProgressEvent<FileReader>) {
      const preview = document.getElementById('imagePreview');
      if (preview && e.target?.result) {
        preview.innerHTML = `<img src="${e.target.result}" style="max-width: 100px; max-height: 100px; border-radius: 10px;">`;
      }
    };
    reader.readAsDataURL(file);
  }
}

function addMessage(content: string, isUser: boolean = false, isHtml: boolean = false): HTMLDivElement {
  const messagesContainer = document.getElementById('chatMessages');
  if (!messagesContainer) throw new Error('Chat messages container not found');

  const message = document.createElement('div');
  message.className = `message ${isUser ? 'user' : 'bot'}`;

  if (isHtml) {
    message.innerHTML = content;
  } else {
    message.textContent = content;
  }

  messagesContainer.appendChild(message);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;

  // Save to history
  chatHistory.push({ content, isUser, isHtml, timestamp: Date.now() });
  saveChatHistory();

  return message;
}

function showTyping(): void {
  const messagesContainer = document.getElementById('chatMessages');
  if (!messagesContainer) return;

  const typing = document.createElement('div');
  typing.className = 'message bot typing';
  typing.id = 'typingIndicator';
  typing.innerHTML = `
    <div class="typing-dot"></div>
    <div class="typing-dot"></div>
    <div class="typing-dot"></div>
  `;
  messagesContainer.appendChild(typing);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

function hideTyping(): void {
  const typing = document.getElementById('typingIndicator');
  if (typing) typing.remove();
}

function animateTyping(): void {
  showTyping();
  setTimeout(() => {
    hideTyping();
  }, 1500);
}

async function sendMessage(): Promise<void> {
  const input = document.getElementById('chatInput') as HTMLInputElement;
  const sendBtn = document.getElementById('sendBtn') as HTMLButtonElement;
  if (!input || !sendBtn) return;

  const message = input.value.trim();

  if (!message || isTyping) return;

  // Add user message
  addMessage(message, true);
  input.value = '';

  // Disable input while processing
  isTyping = true;
  sendBtn.disabled = true;
  showTyping();

  try {
    let response = '';

    if (message.startsWith('/price')) {
      response = await fetchPrice();
    } else if (message.startsWith('/image ')) {
      const prompt = message.replace('/image ', '');
      response = await generateImage(prompt);
    } else if (message.startsWith('/search ')) {
      const query = message.replace('/search ', '');
      response = await searchWeb(query);
    } else if (message.startsWith('/meme')) {
      const imageInput = document.getElementById('imageInput') as HTMLInputElement;
      const description = message.replace('/meme ', '').trim();

      if (imageInput?.files?.[0]) {
        response = await generateMemeFromImage(imageInput.files[0], description);
        // Clear the image after use
        imageInput.value = '';
        const imagePreview = document.getElementById('imagePreview');
        if (imagePreview) imagePreview.innerHTML = '';
        const fileUploadArea = document.getElementById('fileUploadArea');
        if (fileUploadArea) fileUploadArea.style.display = 'none';
      } else if (description) {
        response = await generateMeme(description);
      } else {
        response = "Please provide a meme description or upload an image!";
      }
    } else if (message.startsWith('/ask ')) {
      const question = message.replace('/ask ', '');
      response = await askAI(question);
    } else if (!message.startsWith('/')) {
      // Default to ask command
      response = await askAI(message);
    } else {
      response = "Unknown command. Try /ask, /search, /image, /meme, or /price";
    }

    hideTyping();
    addMessage(response, false, response.includes('<'));

  } catch (error) {
    console.error('Error:', error);
    hideTyping();
    addMessage('🛸 Cosmic interference detected! Please try again.', false);
  } finally {
    isTyping = false;
    sendBtn.disabled = false;
    input.focus();
  }
}

// API Integration Functions

async function askAI(question: string): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: question, useSearch: false })
    });

    const data: ChatResponse = await response.json();
    return data.message || 'Quantum processors overloaded!';
  } catch (error) {
    console.error('AI Error:', error);
    return '🛸 Unable to connect to the mothership!';
  }
}

async function searchWeb(query: string): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });

    const data: SearchResponse = await response.json();

    if (data.results && data.results.length > 0) {
      let html = `<div class="search-results">`;
      html += `<p>${data.message}</p>`;
      html += `<div style="margin-top: 10px; font-size: 12px;">`;
      data.results.slice(0, 3).forEach(result => {
        html += `<div style="margin: 5px 0;">
          <a href="${result.url}" target="_blank" style="color: var(--primary);">
            ${result.title}
          </a>
        </div>`;
      });
      html += `</div></div>`;
      return html;
    }

    return data.message || 'No results found in Earth\'s databases!';
  } catch (error) {
    console.error('Search Error:', error);
    return '🌍 Earth\'s internet is experiencing solar interference!';
  }
}

async function generateImage(prompt: string): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt })
    });

    const data: ImageResponse = await response.json();

    if (data.imageUrl) {
      return `
        <div class="generated-image">
          <p>🎨 Quantum synthesis complete!</p>
          <img src="${data.imageUrl}" class="image-preview"
               onclick="window.open('${data.imageUrl}', '_blank')">
          <p style="font-size: 12px; margin-top: 5px;">"${prompt}"</p>
        </div>
      `;
    }

    return data.message || 'Holographic projector malfunction!';
  } catch (error) {
    console.error('Image Error:', error);
    return '👽 Quantum renderer offline!';
  }
}

async function generateMeme(prompt: string): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/meme`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: prompt })
    });

    const data: MemeResponse = await response.json();

    if (data.imageUrl) {
      return `
        <div class="generated-meme">
          <p>😄 ${data.caption}</p>
          <img src="${data.imageUrl}" class="image-preview"
               onclick="window.open('${data.imageUrl}', '_blank')">
          <div style="margin-top: 10px;">
            <button class="command-btn" onclick="setCommand('meme')">Create Another</button>
          </div>
        </div>
      `;
    }

    return data.message || 'Meme matrix collapsed!';
  } catch (error) {
    console.error('Meme Error:', error);
    return '🌌 Meme wormhole unstable!';
  }
}

async function generateMemeFromImage(file: File, description: string): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('description', description);

    const response = await fetch(`${API_BASE_URL}/meme-from-image`, {
      method: 'POST',
      body: formData
    });

    const data: MemeResponse = await response.json();

    if (data.imageUrl) {
      return `
        <div class="generated-meme">
          <p>🎨 ${data.caption}</p>
          <img src="${data.imageUrl}" class="image-preview"
               onclick="window.open('${data.imageUrl}', '_blank')">
        </div>
      `;
    }

    return data.message || 'Meme synthesis failed!';
  } catch (error) {
    console.error('Meme from Image Error:', error);
    return '👽 Image processing error!';
  }
}

async function fetchPrice(): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/price`);
    const data: PriceResponse = await response.json();

    if (data.success && data.data) {
      const priceData = data.data;
      const changeEmoji = priceData.change24h >= 0 ? '🚀' : '💫';

      return `
        <div class="price-display">
          <h4>XRP Galactic Exchange ${changeEmoji}</h4>
          <p>Earth Price: <strong>$${priceData.price}</strong></p>
          <p>24h Quantum Flux: <strong>${priceData.change24h >= 0 ? '+' : ''}${priceData.change24h.toFixed(2)}%</strong></p>
          <p style="margin-top: 10px; font-style: italic;">Still cheaper than antimatter! 👽</p>
        </div>
      `;
    }

    return data.message || 'Galactic market offline!';
  } catch (error) {
    console.error('Price Error:', error);
    // Fallback to direct CoinGecko API
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ripple&vs_currencies=usd&include_24hr_change=true');
      const data = await response.json();
      const xrpData = data.ripple;

      return `
        <div class="price-display">
          <h4>XRP Galactic Exchange 🚀</h4>
          <p>Earth Price: <strong>$${xrpData.usd}</strong></p>
          <p>24h Flux: <strong>${xrpData.usd_24h_change >= 0 ? '+' : ''}${xrpData.usd_24h_change.toFixed(2)}%</strong></p>
        </div>
      `;
    } catch (fallbackError) {
      return '🛸 Galactic market connection lost!';
    }
  }
}

// Chat history management
function saveChatHistory(): void {
  try {
    localStorage.setItem('aox_chat_history', JSON.stringify(chatHistory.slice(-50))); // Keep last 50 messages
  } catch (error) {
    console.error('Error saving chat history:', error);
  }
}

function loadChatHistory(): void {
  try {
    const saved = localStorage.getItem('aox_chat_history');
    if (saved) {
      chatHistory = JSON.parse(saved) as ChatMessage[];
    }
  } catch (error) {
    console.error('Error loading chat history:', error);
  }
}

// Auto-save settings
function saveSettings(settings: BotSettings): void {
  try {
    localStorage.setItem('aox_bot_settings', JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving settings:', error);
  }
}

function loadSettings(): BotSettings {
  try {
    const saved = localStorage.getItem('aox_bot_settings');
    return saved ? JSON.parse(saved) as BotSettings : {};
  } catch (error) {
    console.error('Error loading settings:', error);
    return {};
  }
}

// Export for use in other scripts if needed
const AOXBot: AOXBotAPI = {
  toggleBot,
  sendMessage,
  setCommand,
  askAI,
  searchWeb,
  generateImage,
  generateMeme,
  fetchPrice
};

window.AOXBot = AOXBot;

// Mobile menu toggle
function toggleMobileMenu(): void {
  const hamburger = document.querySelector('.hamburger');
  const mobileMenu = document.getElementById('mobileMenu');
  hamburger?.classList.toggle('active');
  mobileMenu?.classList.toggle('active');
}

// Navbar scroll effect
window.addEventListener('scroll', function() {
  const navbar = document.getElementById('navbar');
  if (window.scrollY > 50) {
    navbar?.classList.add('scrolled');
  } else {
    navbar?.classList.remove('scrolled');
  }
});

// Copy contract address
function copyContract(): void {
  const contractElement = document.getElementById('contractAddress');
  if (!contractElement) return;

  const contractText = contractElement.textContent?.replace('Contract: ', '') || '';
  if (contractText !== 'Coming Soon...') {
    navigator.clipboard.writeText(contractText);
    const copyBtn = document.querySelector('.copy-btn');
    if (copyBtn) {
      copyBtn.textContent = 'COPIED!';
      setTimeout(() => {
        copyBtn.textContent = 'COPY';
      }, 2000);
    }
  }
}

// Smooth scrolling for navigation links
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function (this: HTMLAnchorElement, e: Event) {
    e.preventDefault();
    const target = document.querySelector(this.getAttribute('href') || '');
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  });
});

// Parallax effect for hero elements
window.addEventListener('scroll', () => {
  const scrolled = window.pageYOffset;
  const parallax = document.querySelector('.alien-image') as HTMLElement;
  if (parallax) {
    const speed = 0.5;
    parallax.style.transform = `translateY(${scrolled * speed}px)`;
  }
});

// Copy token contract address
function copyTokenContract(): void {
  const contractElement = document.getElementById('tokenContractAddress');
  if (!contractElement) return;

  const contractText = contractElement.textContent || '';
  if (contractText !== 'TBA - Awaiting Horizon Deployment') {
    navigator.clipboard.writeText(contractText);
    const copyBtn = document.querySelector('.copy-token-btn');
    if (copyBtn) {
      copyBtn.textContent = 'COPIED!';
      setTimeout(() => {
        copyBtn.textContent = 'COPY';
      }, 2000);
    }
  }
}

// Wallet functions
function initializeWalletSection(): void {
  // Check if wallet section exists in DOM
  const walletSection = document.getElementById('wallet');
  if (walletSection) {
    nftViewer.init('nftGallery');
    updateWalletUI();
  }

  // Initialize header wallet button
  updateHeaderWallet();
}

function updateHeaderWallet(): void {
  const navConnectBtn = document.getElementById('navConnectWallet');
  const mobileConnectBtn = document.getElementById('mobileConnectWallet');
  const navWalletInfo = document.getElementById('navWalletInfo');
  const mobileWalletInfo = document.getElementById('mobileWalletInfo');

  if (xamanAuth.isLoggedIn()) {
    // Hide connect buttons, show wallet info
    if (navConnectBtn) navConnectBtn.style.display = 'none';
    if (mobileConnectBtn) mobileConnectBtn.style.display = 'none';
    if (navWalletInfo) navWalletInfo.style.display = 'flex';
    if (mobileWalletInfo) mobileWalletInfo.style.display = 'flex';

    // Update address
    const address = xamanAuth.getAddress();
    const navAddress = document.getElementById('navWalletAddress');
    const mobileAddress = document.getElementById('mobileWalletAddress');
    if (address) {
      const shortAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;
      if (navAddress) navAddress.textContent = shortAddress;
      if (mobileAddress) mobileAddress.textContent = shortAddress;
    }

    // Fetch and update balances
    updateHeaderBalances();
  } else {
    // Show connect buttons, hide wallet info
    if (navConnectBtn) navConnectBtn.style.display = 'block';
    if (mobileConnectBtn) mobileConnectBtn.style.display = 'block';
    if (navWalletInfo) navWalletInfo.style.display = 'none';
    if (mobileWalletInfo) mobileWalletInfo.style.display = 'none';
  }
}

async function updateHeaderBalances(): Promise<void> {
  const address = xamanAuth.getAddress();
  if (!address) return;

  try {
    // Fetch AOX token balance
    const aoxBalance = await fetchAOXBalance(address);
    const navAOXBalance = document.getElementById('navAOXBalance');
    const mobileAOXBalance = document.getElementById('mobileAOXBalance');
    const balanceText = `${aoxBalance} AOX`;
    if (navAOXBalance) {
      navAOXBalance.textContent = balanceText;
    }
    if (mobileAOXBalance) {
      mobileAOXBalance.textContent = balanceText;
    }
  } catch (error) {
    console.error('Error fetching balances:', error);
  }
}

async function fetchAOXBalance(address: string): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/xaman/balance/${address}`);
    const data = await response.json();

    if (data.success && data.balance) {
      return data.balance;
    }

    return '0';
  } catch (error) {
    console.error('Error fetching AOX balance:', error);
    return '--';
  }
}

function handleNavWalletClick(): void {
  // Just trigger login without scrolling
  if (!xamanAuth.isLoggedIn()) {
    loginWithXaman();
  }
}

async function loginWithXaman(): Promise<void> {
  const loginBtn = document.getElementById('xamanLoginBtn') as HTMLButtonElement;
  if (loginBtn) {
    loginBtn.textContent = '⏳ Connecting...';
    loginBtn.disabled = true;
  }

  try {
    const result = await xamanAuth.login();

    if (result.success && result.address) {
      if (loginBtn) {
        loginBtn.textContent = '✅ Connected';
      }
      updateWalletUI();
      updateHeaderWallet(); // Update header wallet info
      await nftViewer.loadUserNFTs();
    } else {
      showError(result.error || 'Login failed');
      if (loginBtn) {
        loginBtn.textContent = '🔐 Login with Xaman';
        loginBtn.disabled = false;
      }
    }
  } catch (error) {
    console.error('Login error:', error);
    showError('Failed to connect with Xaman. Please try again.');
    if (loginBtn) {
      loginBtn.textContent = '🔐 Login with Xaman';
      loginBtn.disabled = false;
    }
  }
}

function logoutWallet(): void {
  xamanAuth.logout();
  nftViewer.clear();
  updateWalletUI();
  updateHeaderWallet(); // Update header wallet info
}

function handleDisconnect(event?: Event): void {
  if (event) {
    event.stopPropagation();
  }
  logoutWallet();
}

function updateWalletUI(): void {
  const walletStatus = document.getElementById('walletStatus');
  const loginBtn = document.getElementById('xamanLoginBtn');
  const logoutBtn = document.getElementById('xamanLogoutBtn');
  const walletAddress = document.getElementById('walletAddress');

  if (xamanAuth.isLoggedIn()) {
    if (walletStatus) walletStatus.textContent = 'Connected';
    if (loginBtn) loginBtn.style.display = 'none';
    if (logoutBtn) logoutBtn.style.display = 'inline-block';
    if (walletAddress) {
      const address = xamanAuth.getAddress() || '';
      walletAddress.textContent = `${address.slice(0, 8)}...${address.slice(-8)}`;
      walletAddress.style.display = 'block';
    }
  } else {
    if (walletStatus) walletStatus.textContent = 'Not Connected';
    if (loginBtn) loginBtn.style.display = 'inline-block';
    if (logoutBtn) logoutBtn.style.display = 'none';
    if (walletAddress) walletAddress.style.display = 'none';
  }
}

// Expose global functions
window.toggleBot = toggleBot;
window.toggleMobileMenu = toggleMobileMenu;
window.copyContract = copyContract;
window.copyTokenContract = copyTokenContract;
window.setCommand = setCommand;
window.handleKeyPress = handleKeyPress;
window.sendMessage = sendMessage;
window.blogUI = blogUI;
window.loginWithXaman = loginWithXaman;
window.logoutWallet = logoutWallet;
window.handleNavWalletClick = handleNavWalletClick;
window.handleDisconnect = handleDisconnect;
